import { Controller } from "@hotwired/stimulus"

const MAX_FILES = 3
// 서버 상한과 같은 값이어야 한다 — CoffeeUrlParser::UPLOADED_IMAGE_MAX_BYTES.
// 여기서 거르는 것은 보안이 아니라 예의다. 진짜 경계는 서버와 프록시에 있고
// (config/deploy.yml의 proxy.buffering.max_request_body), 이 검사는 40MB를 다 올린
// 뒤에 413을 받는 대신 고르는 자리에서 바로 알려주기 위한 것이다.
// 두 값이 어긋나면 test/javascript/upload_limits.test.mjs가 잡는다.
const MAX_FILE_BYTES = 12_000_000
const MAX_FILE_LABEL = `${Math.floor(MAX_FILE_BYTES / 1_000_000)}MB`

export default class extends Controller {
  static targets = ["input", "dropzone", "summary", "submit"]

  connect() {
    this.renderSummary()
    this.updateSubmitState()
  }

  openFileDialog(event) {
    // Allow keyboard/click to open the native picker from the dropzone surface.
    if (event.type === "keydown" && !["Enter", " "].includes(event.key)) return
    event.preventDefault()
    this.inputTarget.click()
  }

  onInputChange() {
    const original = Array.from(this.inputTarget.files || [])
    const selection = this.normalizeImageFiles(original)

    if (selection.files.length !== original.length) {
      this.assignFiles(selection.files)
    }
    this.renderSummary(this.rejectionNote(selection))
    this.updateSubmitState()
  }

  onDragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("is-dragover")
  }

  onDragLeave(event) {
    if (this.dropzoneTarget.contains(event.relatedTarget)) return
    this.dropzoneTarget.classList.remove("is-dragover")
  }

  onDrop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("is-dragover")

    const selection = this.normalizeImageFiles(Array.from(event.dataTransfer?.files || []))
    const note = this.rejectionNote(selection)

    // 남길 파일이 하나도 없으면 이미 고른 것을 지우지 않는다 — 잘못 끌어다 놓은 것이
    // 앞선 선택을 날리면 손해가 크다. 왜 안 들어갔는지만 알린다.
    if (selection.files.length === 0) {
      this.renderSummary(note || "이미지 파일만 업로드할 수 있어요.")
      return
    }

    this.assignFiles(selection.files)
    this.renderSummary(note)
    this.updateSubmitState()
  }

  assignFiles(files) {
    try {
      const dataTransfer = new DataTransfer()
      files.forEach((file) => dataTransfer.items.add(file))
      this.inputTarget.files = dataTransfer.files
    } catch (_error) {
      // Keep graceful behavior when assignment APIs are restricted.
    }
  }

  renderSummary(note = "") {
    const files = Array.from(this.inputTarget.files || [])
    let base

    if (files.length === 0) {
      base = "선택된 파일 없음"
    } else {
      const names = files.slice(0, 2).map((file) => file.name).join(", ")
      const extra = files.length > 2 ? ` 외 ${files.length - 2}개` : ""
      base = `${files.length}개 선택됨: ${names}${extra}`
    }

    this.summaryTarget.textContent = note ? `${base} — ${note}` : base
  }

  updateSubmitState() {
    if (!this.hasSubmitTarget) return

    const ready = Array.from(this.inputTarget.files || []).length > 0
    this.submitTarget.disabled = !ready
  }

  // 무엇이 왜 빠졌는지 따로 세어 둔다. 개수만 맞추면 "3장 골랐는데 1장만 들어갔다"가
  // 되고 사용자는 이유를 모른 채 다시 시도한다.
  normalizeImageFiles(files) {
    const all = Array.from(files || [])
    const images = all.filter((file) => file.type.startsWith("image/"))
    const withinSize = images.filter((file) => file.size <= MAX_FILE_BYTES)

    return {
      files: withinSize.slice(0, MAX_FILES),
      rejectedType: all.length - images.length,
      rejectedSize: images.length - withinSize.length,
      rejectedCount: Math.max(0, withinSize.length - MAX_FILES)
    }
  }

  rejectionNote({ rejectedType, rejectedSize, rejectedCount }) {
    const notes = []
    if (rejectedType > 0) notes.push("이미지가 아닌 파일은 뺐어요")
    if (rejectedSize > 0) notes.push(`${MAX_FILE_LABEL}가 넘는 ${rejectedSize}장은 뺐어요`)
    if (rejectedCount > 0) notes.push(`${MAX_FILES}장까지만 올릴 수 있어요`)
    return notes.join(", ")
  }
}
